import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { fetchCatalogoAreas, type Configuracao } from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Combobox } from '@/components/ui/combobox';
import { classifyError } from '@/lib/errors';

const OUTRA = '__outra__';

/** Resultado da escolha no seletor de áreas. */
export type EscolhaArea =
  | { tipo: 'nova' } // cadastrar do zero (nome/docs/perguntas)
  | { tipo: 'editar'; areaId: string } // área já existente → editar
  | { tipo: 'adotar'; chave: string }; // área do catálogo ainda não criada → adotar

interface AreaPickerProps {
  config: Configuracao;
  disabled?: boolean;
  onEscolher: (escolha: EscolhaArea) => void;
}

/**
 * Seletor único de área de atuação. Escolher uma área comum que já existe abre a
 * edição; uma que ainda não existe é criada a partir do catálogo; "Outra área"
 * abre o cadastro em branco. Substitui o antigo botão "Nova área".
 */
export function AreaPicker({ config, disabled, onEscolher }: AreaPickerProps) {
  const { data: catalogo, error } = useQuery({
    queryKey: ['secretario', 'catalogo-areas'],
    queryFn: fetchCatalogoAreas,
  });

  const options = [
    ...(catalogo ?? []).map((a) => ({ value: a.chave, label: a.nome })),
    { value: OUTRA, label: 'Outra área…' },
  ];

  const escolher = (value: string) => {
    if (!value || disabled) return;
    if (value === OUTRA) {
      onEscolher({ tipo: 'nova' });
      return;
    }
    const cat = catalogo?.find((c) => c.chave === value);
    if (!cat) return;
    const existente = config.areas.find(
      (a) => a.catalogo_chave === cat.chave || a.nome.trim().toLowerCase() === cat.nome.trim().toLowerCase(),
    );
    onEscolher(existente ? { tipo: 'editar', areaId: existente.id } : { tipo: 'adotar', chave: cat.chave });
  };

  return (
    <div className="space-y-2">
      {error != null && (
        // Erro na busca do catálogo não pode ser silencioso: sem isto o seletor
        // mostra só "Outra área…" sem explicar por que as áreas comuns sumiram.
        // O seletor continua utilizável ("Outra área…" cria do zero).
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Não foi possível carregar o catálogo de áreas: {classifyError(error).message}
          </AlertDescription>
        </Alert>
      )}
      <Combobox
        options={options}
        value=""
        onValueChange={escolher}
        placeholder="Escolha a área de atuação"
        searchPlaceholder="Buscar área…"
        emptyMessage="Nenhuma área no catálogo"
      />
    </div>
  );
}
